import SoundManager from "../../../../../lib/html5-software-library/src/ranj/audio/SoundManager";
import Anchor from "../../../../../lib/html5-software-library/src/ranj/layout/Anchor";
import LayoutEvent from "../../../../../lib/html5-software-library/src/ranj/layout/LayoutEvent";
import LayoutManager from "../../../../../lib/html5-software-library/src/ranj/layout/LayoutManager";
import ScreenNavigator from "../../../../../lib/html5-software-library/src/ranj/ui/ScreenNavigator";
import ClickEaterFactory from "../../../core/ui/ClickEaterFactory";
import EmailInterfaceEvent from "../../email/model/EmailInterfaceEvent";
import LaptopNavigateEvent from "../model/LaptopNavigateEvent";
import LaptopSetupNavigatorEvent from "../model/LaptopSetupNavigatorEvent";
import LaptopSkinKeyRegistry from "../model/LaptopSkinKeyRegistry";
import LaptopSoundType from "../sound/LaptopSoundType";
import AbstractLaptopMediator from "./AbstractLaptopMediator";

export default class LaptopMediator extends AbstractLaptopMediator {
    /**
     * @constructor
     * @param {createjs.MovieClip} target 
     * @param {soma.EventDispatcher} dispatcher
     * @param {LayoutManager} layoutManager
     * @param {ClickEaterFactory} clickEaterFactory
     * @param {ScreenNavigator} screenNavigator
     * @param {SoundManager} soundManager
     */
    constructor(target, dispatcher, layoutManager, clickEaterFactory, screenNavigator, soundManager) {
        super(target, dispatcher, layoutManager, clickEaterFactory);

        this._screenNavigator = screenNavigator;
        this._soundManager = soundManager;

        this._locked = false;
    }

    initialize() {
        super.initialize();

        this._setupSkin();

        this._hide(false);

        this.registerEventListener(LaptopNavigateEvent.LAPTOP_SHOW_REQUEST, this._handleLaptopShowRequest.bind(this));
        this.registerEventListener(LaptopNavigateEvent.LAPTOP_HIDE_REQUEST, this._handleLaptopHideRequest.bind(this));

        this.registerEventListener(LayoutEvent.CHANGED, this._handleLayoutChanged.bind(this));

        this.registerEventListener(EmailInterfaceEvent.LOCK_INTERFACE, this._handleLockInterface.bind(this));
        this.registerEventListener(EmailInterfaceEvent.UNLOCK_INTERFACE, this._handleUnlockInterface.bind(this));

        this.registerEventListener(LaptopNavigateEvent.SHOW_OVERLAY, this._handleShowOverlay.bind(this));
        this.registerEventListener(LaptopNavigateEvent.HIDE_OVERLAY, this._handleHideOverlay.bind(this));


    }

    destroy() {
        createjs.Tween.removeTweens(this._deviceSkin.closeButton);

        this._deviceSkin = null;

        this._overlay.removeEventListener('click', this._onOverlayClickedBound);

        super.destroy();
    }


    _setupSkin() {
        // Overlay to prevent click through
        this._overlay = new lib.LaptopAssets.LaptopOverlay();
        this._target.addChild(this._overlay);
        this._onOverlayClickedBound = this._onOverlayClicked.bind(this);
        this._overlay.addEventListener('click', this._onOverlayClickedBound);
        this._overlay.alpha = 0;

        // this.stretchBackground(this._overlay);

        // Device skin
        this._deviceSkinContainer = new createjs.Container();
        this._target.addChild(this._deviceSkinContainer);
        // this.anchorObject(this._deviceSkinContainer, Anchor.B);

        this._deviceSkin = new lib.LaptopAssets.LaptopDeviceSkin();
        this._deviceSkinContainer.addChild(this._deviceSkin);
        this._deviceSkin.initialX = this._deviceSkin.x;
        this._deviceSkin.initialY = this._deviceSkin.y;

        this._deviceSkin.closeButton.gotoAndStop(0);
        this._deviceSkin.closeButton.snapToPixel = false;
        this._deviceSkin.closeButton.initialY = this._deviceSkin.closeButton.y;
        createjs.Tween.get(this._deviceSkin.closeButton, { loop: -1 })
            .to({ y: this._deviceSkin.closeButton.initialY + 5 }, 700)
            .to({ y: this._deviceSkin.closeButton.initialY }, 300);

        // Determine viewport based on screen canvas (defined in the FLA)
        const screenCanvas = this._deviceSkin.screenCanvas;
        this._screenViewport = new createjs.Rectangle(screenCanvas.x, screenCanvas.y, screenCanvas.nominalBounds.width, screenCanvas.nominalBounds.height);

        screenCanvas.addEventListener('click', () => { });

        const navigatorContextView = new createjs.Container();
        navigatorContextView.x = this._screenViewport.x;
        navigatorContextView.y = this._screenViewport.y;
        this._deviceSkin.addChild(navigatorContextView);

        // Configure and 'attach' the laptop navigator to this mediator
        const setupNavigatorEvent = new LaptopSetupNavigatorEvent(LaptopSetupNavigatorEvent.PERFORM, navigatorContextView, new createjs.Point(this._screenViewport.width, this._screenViewport.height))
        this.dispatch(setupNavigatorEvent.type, { data: setupNavigatorEvent });

        this.gotoScreen(LaptopSkinKeyRegistry.LAPTOP_MAIN_SCREEN_SKIN);
    }


    _handleShowOverlay() {
        if(this._isShowing) {
            createjs.Tween.get(this._overlay).to({ alpha: 1 }, 350, createjs.Ease.sineInOut);
        }
    }

    _handleHideOverlay() {
        createjs.Tween.get(this._overlay).to({ alpha: 0 }, 450, createjs.Ease.sineInOut);
    }

    _onOverlayClicked() {
        if (this._locked) {
            return;
        }

        this.dispatch(LaptopNavigateEvent.LAPTOP_HIDE_REQUEST);
    }

    _handleLayoutChanged() {
        if (!this._isShowing) {
            this._hide(false);
        }
    }

    _handleLaptopShowRequest(evt) {
        const data = evt.params?.data;
        const needsOverlay = data?.needsOverlay;
        this._show(needsOverlay);

        this.gotoScreen(LaptopSkinKeyRegistry.LAPTOP_MAIN_SCREEN_SKIN);
    }

    _handleLaptopHideRequest() {
        this._hide();
    }

    _show(showOverlay = true) {
        this.playSound(LaptopSoundType.OPEN);

        createjs.Tween.get(this._deviceSkin)
            .to({ y: this._deviceSkin.initialY }, 350, createjs.Ease.sineInOut);

        const targetOverlayAlpha = showOverlay ? 1 : .1; // show .1 alpha so that the click listener still eats the clicks
        createjs.Tween.get(this._overlay).to({ alpha: targetOverlayAlpha }, 350, createjs.Ease.sineInOut);

        this._isShowing = true;
    }

    _hide(animated = true) {
        if (animated) {
            this.disableInteraction();

            this.playSound(LaptopSoundType.CLOSE);

            createjs.Tween.get(this._deviceSkin)
                .to({ y: this._screenNavigator.getScreenHeight() }, 350, createjs.Ease.sineInOut);

            createjs.Tween.get(this._overlay).to({ alpha: 0 }, 350, createjs.Ease.sineInOut)
                .call(() => {
                    this.enableInteraction(); // Be mindful that removing this tween will cause interaction to not be re-enabled.
                });
        } else {
            this._deviceSkin.y = this._screenNavigator.getScreenHeight();
            this._overlay.alpha = 0;
        }

        this._isShowing = false;
    }

    _handleLockInterface() {
        this._locked = true;
        this._deviceSkin.closeButton.visible = false;
    }

    _handleUnlockInterface() {
        this._locked = false;
        this._deviceSkin.closeButton.visible = true;
    }
}